import { type Profile, isControlled, profileRank } from './profiles';

export const REQUIREMENT_TYPES = ['FR', 'NFR', 'SEC', 'DATA', 'UX'] as const;
export type RequirementType = (typeof REQUIREMENT_TYPES)[number];

export const PRIORITIES = ['Must', 'Should', 'Could'] as const;
export type Priority = (typeof PRIORITIES)[number];

export type Example = { given: string; when: string; then: string };

export type Requirement = {
  id: string;
  type: RequirementType;
  statement: string;
  rationale?: string;
  source?: string;
  fit?: string;
  priority: Priority;
  examples?: Example[];
  misuse?: string | null;
  /** Where an assistant suggestion in it came from. */
  provenance?: { tool: string; model: string; prompt_version: string; at?: string };
};

export type OpenQuestion = { text: string; owner?: string; due?: string };

/** `docs/requirements.json` schema v2 (journey spec §7.2). */
export type RequirementsV2 = {
  schema_version: 2;
  version: number;
  project_name: string;
  outcome_ref: string;
  requirements: Requirement[];
  open_questions: OpenQuestion[];
  acceptance_test_owner: string[];
};

export const REQUIREMENTS_PATH = 'docs/requirements.json';
export const REQUIREMENTS_V1_PATH = 'docs/requirements.v1.json';
export const OUTCOME_REF = '.vivicta/asdp/gate1.json#outcome';

export const VAGUE_WORDS = [
  'fast', 'quick', 'quickly', 'easy', 'easily', 'user-friendly', 'intuitive',
  'efficient', 'efficiently', 'secure', 'securely', 'robust', 'scalable', 'good',
  'appropriate', 'appropriately', 'seamless', 'seamlessly', 'smooth', 'smoothly',
] as const;

const PASS_FAIL_WORDS = ['passes', 'pass', 'fails', 'fail', 'every', 'all', 'none', 'no'];

const words = (text: string) => text.toLowerCase().match(/[a-z][a-z-]*/g) ?? [];

/** Not vague, and either a number or a pass/fail test result. */
export function isMeasurableFit(fit: string | undefined): boolean {
  const text = fit?.trim() ?? '';
  if (!text) return false;
  const hasDigit = /\d/.test(text);
  const tokens = words(text);
  const vague = tokens.some(w => (VAGUE_WORDS as readonly string[]).includes(w));
  if (vague && !hasDigit) return false;
  return hasDigit || tokens.some(w => PASS_FAIL_WORDS.includes(w));
}

export const REQUIREMENT_CHECKS = [
  'fit',
  'source',
  'example',
  'misuse',
] as const;
export type RequirementCheckId = (typeof REQUIREMENT_CHECKS)[number];

export type RequirementCheck = {
  id: RequirementCheckId;
  label: string;
  passed: boolean;
  /** Required checks block Gate 2; the others are advice. */
  required: boolean;
  /** Undefined when the check doesn't apply to this requirement. */
  applies: boolean;
  hint: string;
};

const completeExample = (e: Example) =>
  Boolean(e.given?.trim() && e.when?.trim() && e.then?.trim());

/** The live quality checks for one requirement (journey spec §3.4). */
export function requirementChecks(
  requirement: Requirement,
  profile: Profile,
): RequirementCheck[] {
  const controlled = isControlled(profile);
  const atLeastP1 = profileRank(profile) >= profileRank('P1');
  const isMust = requirement.priority === 'Must';
  const isSec = requirement.type === 'SEC';
  return [
    {
      id: 'fit',
      label: 'Measurable fit',
      passed: isMeasurableFit(requirement.fit),
      required: true,
      applies: true,
      hint: 'Use a number, or a pass/fail test result, instead of words like “fast” or “easy”.',
    },
    {
      id: 'source',
      label: 'Has a source',
      passed: Boolean(requirement.source?.trim()),
      required: true,
      applies: true,
      hint: 'Name the person it came from.',
    },
    {
      id: 'example',
      label: 'Example',
      passed: (requirement.examples ?? []).some(completeExample),
      required: isMust && controlled,
      applies: isMust && atLeastP1,
      hint: 'Add a complete given / when / then.',
    },
    {
      id: 'misuse',
      label: 'Misuse case',
      passed: Boolean(requirement.misuse?.trim()),
      required: isSec && controlled,
      applies: isSec && atLeastP1,
      hint: 'Describe the abuse, the expected response and the recovery.',
    },
  ];
}

export const failingRequiredChecks = (requirement: Requirement, profile: Profile) =>
  requirementChecks(requirement, profile).filter(c => c.required && !c.passed);

/** What Gate 2 still needs; empty when ready. */
export function requirementsReadiness(
  doc: Pick<RequirementsV2, 'requirements' | 'open_questions' | 'acceptance_test_owner'>,
  profile: Profile,
): string[] {
  const problems: string[] = [];
  if (doc.requirements.length === 0) problems.push('Add at least one requirement');
  for (const requirement of doc.requirements) {
    const failing = failingRequiredChecks(requirement, profile);
    if (failing.length > 0) {
      problems.push(
        `${requirement.id}: ${failing.map(c => c.label.toLowerCase()).join(', ')}`,
      );
    }
  }
  doc.open_questions.forEach((question, index) => {
    if (!question.owner || !question.due) {
      problems.push(`Open question ${index + 1} needs an owner and a due date`);
    }
  });
  if (doc.acceptance_test_owner.length === 0) {
    problems.push('Name a Tester / QA to own the acceptance-test baseline');
  }
  return problems;
}

/**
 * The lowest free id of a type (ASDP-SPC-03). `taken` holds the ids in use and
 * every id ever sent for a decision, which is never given to another
 * requirement; a number deleted before it was sent is free again.
 */
export function nextRequirementId(
  type: RequirementType,
  taken: readonly string[],
): string {
  const used = new Set(
    taken
      .map(id => new RegExp(`^${type}-(\\d+)$`).exec(id)?.[1])
      .filter((n): n is string => n !== undefined)
      .map(Number),
  );
  let next = 1;
  while (used.has(next)) next += 1;
  return `${type}-${String(next).padStart(2, '0')}`;
}

/** Today's v1 shape (RequirementsJson), for templates that haven't moved to v2. */
export type RequirementsV1 = {
  project_name: string;
  functional_requirements: Array<{
    id: string;
    title: string;
    description: string;
    priority: string;
    acceptanceCriteria: string[];
  }>;
  non_functional_requirements: Array<{
    id: string;
    category: string;
    description: string;
    target: string;
  }>;
  security_requirements: string[];
  data_requirements: string[];
  ux_requirements: string[];
};

const exampleText = (e: Example) => `Given ${e.given}, when ${e.when}, then ${e.then}`;

/** Down-converts v2 for legacy templates (journey spec §7.2). */
export function toRequirementsV1(doc: RequirementsV2): RequirementsV1 {
  const of = (type: RequirementType) => doc.requirements.filter(r => r.type === type);
  return {
    project_name: doc.project_name,
    functional_requirements: of('FR').map(r => ({
      id: r.id,
      title: r.statement,
      description: r.rationale ?? '',
      priority: r.priority,
      acceptanceCriteria: [
        ...(r.fit ? [r.fit] : []),
        ...(r.examples ?? []).filter(completeExample).map(exampleText),
      ],
    })),
    non_functional_requirements: of('NFR').map(r => ({
      id: r.id,
      category: 'general',
      description: r.statement,
      target: r.fit ?? '',
    })),
    security_requirements: of('SEC').map(r => r.statement),
    data_requirements: of('DATA').map(r => r.statement),
    ux_requirements: of('UX').map(r => r.statement),
  };
}

export const emptyRequirements = (projectName: string): RequirementsV2 => ({
  schema_version: 2,
  version: 1,
  project_name: projectName,
  outcome_ref: OUTCOME_REF,
  requirements: [],
  open_questions: [],
  acceptance_test_owner: [],
});
